/**
 * IconButton Component
 *
 * An icon-only button for actions like close, collapse/expand, navigation,
 * and menu toggles. Provides consistent styling and accessibility for
 * icon-only interactive elements.
 *
 * WCAG AAA Compliant:
 * - Required aria-label for screen readers
 * - Keyboard accessible (Enter/Space)
 * - Focus indicator for keyboard navigation
 * - Proper disabled state handling
 *
 * @example
 * ```tsx
 * // Close button
 * <IconButton
 *   icon={<Icon name="close" />}
 *   variant="close"
 *   aria-label="Close modal"
 *   onClick={handleClose}
 * />
 *
 * // Navigation button
 * <IconButton
 *   icon={<Icon name="chevronLeft" />}
 *   variant="nav"
 *   aria-label="Previous month"
 *   onClick={handlePrevMonth}
 * />
 *
 * // Toggle button
 * <IconButton
 *   icon={<Icon name="chevronLeft" />}
 *   variant="toggle"
 *   aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
 *   aria-expanded={!collapsed}
 *   onClick={handleToggle}
 * />
 * ```
 */

import React, { useCallback, memo } from "react";
import classNames from "classnames";
import type { IconButtonProps } from "./types";
import styles from "./IconButton.module.css";

export const IconButton = memo(
  React.forwardRef<HTMLButtonElement, IconButtonProps>(
    (
      {
        icon,
        variant = "default",
        size = "md",
        shape = "circle",
        disabled = false,
        "aria-label": ariaLabel,
        "aria-expanded": ariaExpanded,
        "aria-controls": ariaControls,
        onClick,
        className,
        "data-testid": testId,
      },
      ref,
    ) => {
      const handleClick = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
          if (!disabled && onClick) {
            onClick(event);
          }
        },
        [disabled, onClick],
      );

      const handleKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLButtonElement>) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            if (!disabled && onClick) {
              onClick(event as unknown as React.MouseEvent<HTMLButtonElement>);
            }
          }
        },
        [disabled, onClick],
      );

      return (
        <button
          ref={ref}
          type="button"
          className={classNames(
            styles.iconButton,
            styles[variant],
            styles[size],
            styles[shape],
            {
              [styles.disabled]: disabled,
            },
            className,
          )}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          aria-label={ariaLabel}
          aria-expanded={ariaExpanded}
          aria-controls={ariaControls}
          data-testid={testId}
        >
          <span className={styles.iconWrapper}>{icon}</span>
        </button>
      );
    },
  ),
);

IconButton.displayName = "IconButton";

export default IconButton;
